import { useState, useEffect } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import logger from '@/lib/logger';
import { formatCurrency, formatDateTimeAU, formatDateAU, daysUntil, calculateCompleteness } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  AlertCircle, CheckCircle2, Clock, Users, Building2, Link2, 
  ChevronRight, ExternalLink, Edit2, Save, DollarSign, FileText,
  Download, FileDown, Printer
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import NotesBlock from '@/components/NotesBlock';
import CallLogCard from '@/components/CallLogCard';
import CallLogModal from '@/components/modals/CallLogModal';
import TaskModal from '@/components/TaskModal';
import AIClientInsights from '@/components/ai/AIClientInsights';
import { TaskSuggestionsButton } from '@/components/ai/AIInsights';
import MeetingPrepButton from '@/components/ai/MeetingPrepButton';
import FamilyPrincipalInviteButton from '@/components/client/FamilyPrincipalInviteButton';
import InternalAdviserAssignment from '@/components/client/InternalAdviserAssignment';
import { toast } from 'sonner';

export default function ClientOverview() {
  const { client, clientId } = useOutletContext();
  const { token } = useAuth();
  const navigate = useNavigate();
  
  const [workflow, setWorkflow] = useState([]);
  const [links, setLinks] = useState([]);
  const [activity, setActivity] = useState([]);
  const [governance, setGovernance] = useState([]);
  const [entities, setEntities] = useState([]);
  const [individuals, setIndividuals] = useState([]);
  const [advisers, setAdvisers] = useState([]);
  const [callLogs, setCallLogs] = useState([]);
  const [editingNetWorth, setEditingNetWorth] = useState(false);
  const [netWorthValue, setNetWorthValue] = useState(0);
  const [localClient, setLocalClient] = useState(null);
  const [showCallLogModal, setShowCallLogModal] = useState(false);
  const [editingCallLog, setEditingCallLog] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [prefillTaskData, setPrefillTaskData] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [loading, setLoading] = useState(true);

  // Initialize localClient and netWorthValue from client prop
  const displayClient = localClient || client;
  const displayNetWorth = netWorthValue || client?.netWorth || 0;

  // Export handlers
  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const response = await api.exportClientPack(token, clientId, 'html', {
        includeIndividuals: true,
        includeEntities: true,
        includeAdvisers: true,
        includeActivity: true
      });
      
      // Open in new window for printing
      const printWindow = window.open('', '_blank');
      printWindow.document.write(response.data);
      printWindow.document.close();
      toast.success('Client pack opened - use Print (Ctrl+P) to save as PDF');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export client pack');
    }
    setExporting(false);
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const response = await api.exportClientPack(token, clientId, 'csv', {
        includeIndividuals: true,
        includeEntities: true,
        includeAdvisers: true
      });
      
      // Download CSV
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${displayClient?.name || 'client'}_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported successfully');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export CSV');
    }
    setExporting(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!token || !clientId) return;
      setLoading(true);
      try {
        const [workflowRes, linksRes, activityRes, governanceRes, entitiesRes, individualsRes, advisersRes, callLogsRes] = await Promise.all([
          api.getWorkflow(token, clientId),
          api.getLinks(token, clientId),
          api.getActivity(token, clientId),
          api.getGovernance(token, clientId),
          api.getEntities(token, clientId),
          api.getIndividuals(token, clientId),
          api.getAdvisers(token, clientId),
          api.getCallLogs(token, clientId)
        ]);
        setWorkflow(workflowRes.data);
        setLinks(linksRes.data);
        setActivity(activityRes.data);
        setGovernance(governanceRes.data);
        setEntities(entitiesRes.data);
        setIndividuals(individualsRes.data);
        setAdvisers(advisersRes.data);
        setCallLogs(callLogsRes.data);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
      setLoading(false);
    };
    fetchData();
  }, [token, clientId]);

  const handleSaveNetWorth = async () => {
    try {
      const response = await api.updateNetWorth(token, clientId, netWorthValue || displayNetWorth);
      setLocalClient(response.data);
      setEditingNetWorth(false);
    } catch (error) {
      console.error('Failed to update net worth:', error);
    }
  };

  const handleSaveNotes = async (notes) => {
    try {
      const response = await api.updateClientNotes(token, clientId, notes);
      setLocalClient(response.data);
      toast.success('Notes saved');
    } catch (error) {
      console.error('Failed to save notes:', error);
      toast.error('Failed to save notes');
    }
  };

  const handleAddCallLog = async (callLogData) => {
    try {
      const response = await api.createCallLog(token, clientId, callLogData);
      setCallLogs([response.data, ...callLogs]);
      toast.success('Call logged successfully');
    } catch (error) {
      console.error('Failed to create call log:', error);
      toast.error('Failed to log call');
    }
  };

  const handleEditCallLog = async (callLog) => {
    setEditingCallLog(callLog);
    setShowCallLogModal(true);
  };

  const handleUpdateCallLog = async (callLogData) => {
    if (editingCallLog) {
      try {
        const response = await api.updateCallLog(token, clientId, editingCallLog.id, callLogData);
        setCallLogs(callLogs.map(c => c.id === editingCallLog.id ? response.data : c));
        toast.success('Call log updated');
        setEditingCallLog(null);
      } catch (error) {
        console.error('Failed to update call log:', error);
        toast.error('Failed to update call log');
      }
    } else {
      await handleAddCallLog(callLogData);
    }
  };

  const handleDeleteCallLog = async (callLog) => {
    if (!window.confirm('Are you sure you want to delete this call log?')) return;
    try {
      await api.deleteCallLog(token, clientId, callLog.id);
      setCallLogs(callLogs.filter(c => c.id !== callLog.id));
      toast.success('Call log deleted');
    } catch (error) {
      console.error('Failed to delete call log:', error);
      toast.error('Failed to delete call log');
    }
  };

  const handleCreateTaskFromCall = async (callLog) => {
    // Pre-fill task data from the call log
    const prefillData = {
      title: `Follow-up: ${callLog.summary.substring(0, 50)}`,
      dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 1 week from now
      owner: 'TBD',
      status: 'Pending',
      type: 'Key task',
      notes: `From call on ${new Date(callLog.dateTime).toLocaleDateString('en-AU')}:\n\nActions: ${callLog.actions || 'N/A'}\n\nOriginal summary: ${callLog.summary}`,
      isRecurring: false
    };
    
    setPrefillTaskData(prefillData);
    setShowTaskModal(true);
  };

  const handleSendCallSummary = async (callLog) => {
    // Prompt for email address
    const email = window.prompt('Enter recipient email address:', '');
    if (!email) return;
    
    // Basic email validation
    if (!email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    try {
      await api.sendCallSummaryEmail(token, clientId, callLog.id, email, true);
      toast.success(`Summary sent to ${email}`);
    } catch (error) {
      console.error('Failed to send call summary:', error);
      toast.error('Failed to send summary email');
    }
  };

  const handleSaveTask = async (taskData) => {
    try {
      await api.createTask(token, clientId, taskData);
      toast.success('Task created');
      // Refresh workflow
      const workflowRes = await api.getWorkflow(token, clientId);
      setWorkflow(workflowRes.data);
      setShowTaskModal(false);
      setPrefillTaskData(null);
    } catch (error) {
      console.error('Failed to create task:', error);
      toast.error('Failed to create task');
    }
  };

  // Get upcoming tasks (next 30 days)
  const upcomingTasks = workflow.filter(task => {
    const days = daysUntil(task.dueDate);
    return days !== null && days >= 0 && days <= 30;
  }).slice(0, 5);

  // Get links needing review
  const linksNeedingReview = links.filter(l => l.status === 'Needs review' || l.status === 'Broken');

  // Get entities missing authority
  const entitiesMissingAuthority = entities.filter(e => !e.authoritySet);

  // Calculate data completeness
  const completeness = displayClient?.dataCompleteness || {};
  const overallCompleteness = calculateCompleteness(completeness);

  // Skeleton loading state — only show on first fetch
  if (loading) {
    return (
      <div className="animate-fade-in-up" data-testid="client-overview-skeleton">
        {/* Header skeleton */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <Skeleton className="h-8 w-40 mb-2" />
            <Skeleton className="h-4 w-64" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-9 w-32" />
            <Skeleton className="h-9 w-32" />
          </div>
        </div>

        {/* Top stats row */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {[0, 1, 2].map(i => (
            <div key={i} className="bg-white rounded-lg border border-[#E6E0D9] p-4 space-y-3">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-8 w-32" />
              <Skeleton className="h-2 w-full" />
            </div>
          ))}
        </div>

        {/* Two-column body */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            {[0, 1, 2].map(i => (
              <div key={i} className="bg-white rounded-lg border border-[#E6E0D9] p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <Skeleton className="h-5 w-40" />
                  <Skeleton className="h-7 w-20" />
                </div>
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-5/6" />
                <Skeleton className="h-4 w-3/4" />
              </div>
            ))}
          </div>
          <div className="space-y-4">
            {[0, 1].map(i => (
              <div key={i} className="bg-white rounded-lg border border-[#E6E0D9] p-5 space-y-3">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-4/5" />
                <Skeleton className="h-4 w-2/3" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in-up" data-testid="client-overview">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-2xl text-[#2C2420] mb-2">Overview</h1>
          <p className="text-[#5D524D]">Command centre for {displayClient?.name}</p>
        </div>
        
        {/* Export Dropdown */}
        <div className="flex items-center gap-2 flex-wrap">
          <MeetingPrepButton clientId={clientId} clientName={displayClient?.name} />
          <TaskSuggestionsButton clientId={clientId} clientName={displayClient?.name} />
          <FamilyPrincipalInviteButton clientId={clientId} clientName={displayClient?.name} />
          <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="outline" 
              className="caffeine-btn-outline"
              disabled={exporting}
              data-testid="export-dropdown-btn"
            >
              {exporting ? (
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              Export
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuItem onClick={handleExportPDF} data-testid="export-pdf-btn">
              <Printer className="w-4 h-4 mr-2" />
              Print / Save as PDF
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleExportCSV} data-testid="export-csv-btn">
              <FileDown className="w-4 h-4 mr-2" />
              Export to CSV
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        </div>
      </div>

      {/* Action Centre */}
      <div className="caffeine-card p-6 mb-6" data-testid="action-centre">
        <h2 className="font-heading text-lg text-[#2C2420] mb-4">Action Centre</h2>
        <p className="text-sm text-[#8C8078] mb-4">Now / Next / Needs attention</p>
        
        <div className="space-y-3">
          {/* Upcoming Workflow Items */}
          {upcomingTasks.length > 0 && upcomingTasks.map(task => {
            const days = daysUntil(task.dueDate);
            return (
              <div 
                key={task.id}
                className="action-item"
                onClick={() => navigate(`/app/clients/${clientId}/workflow`)}
                data-testid={`action-task-${task.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    days <= 7 ? 'bg-[#D99A6C]/20 text-[#D99A6C]' : 'bg-[#4A3B32]/10 text-[#4A3B32]'
                  }`}>
                    <Clock className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="font-medium text-[#2C2420]">{task.title}</p>
                    <p className="text-sm text-[#8C8078]">
                      Due {formatDateAU(task.dueDate)} • {task.owner}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    days <= 7 ? 'badge-needs-review' : 'bg-[#F0EBE6] text-[#5D524D]'
                  }`}>
                    {days} day{days !== 1 ? 's' : ''}
                  </span>
                  <ChevronRight className="w-4 h-4 text-[#8C8078]" />
                </div>
              </div>
            );
          })}

          {/* Authority Missing */}
          {entitiesMissingAuthority.length > 0 && (
            <div 
              className="action-item"
              onClick={() => navigate(`/app/clients/${clientId}/entities`)}
              data-testid="action-authority-missing"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#C25B5B]/20 text-[#C25B5B]">
                  <AlertCircle className="w-4 h-4" />
                </div>
                <div>
                  <p className="font-medium text-[#2C2420]">Authority/signatories missing</p>
                  <p className="text-sm text-[#8C8078]">
                    {entitiesMissingAuthority.length} entit{entitiesMissingAuthority.length !== 1 ? 'ies' : 'y'} need attention
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="badge-broken">{entitiesMissingAuthority.length}</span>
                <ChevronRight className="w-4 h-4 text-[#8C8078]" />
              </div>
            </div>
          )}

          {/* Links Needing Review */}
          {linksNeedingReview.length > 0 && (
            <div 
              className="action-item"
              onClick={() => navigate(`/app/clients/${clientId}/links`)}
              data-testid="action-links-review"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-[#D99A6C]/20 text-[#D99A6C]">
                  <Link2 className="w-4 h-4" />
                </div>
                <div>
                  <p className="font-medium text-[#2C2420]">Links needing review</p>
                  <p className="text-sm text-[#8C8078]">
                    {linksNeedingReview.length} link{linksNeedingReview.length !== 1 ? 's' : ''} require attention
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="badge-needs-review">{linksNeedingReview.length}</span>
                <ChevronRight className="w-4 h-4 text-[#8C8078]" />
              </div>
            </div>
          )}

          {upcomingTasks.length === 0 && entitiesMissingAuthority.length === 0 && linksNeedingReview.length === 0 && (
            <div className="flex items-center gap-3 p-4 bg-[#6B8E6B]/10 border border-[#6B8E6B]/20 rounded-lg">
              <CheckCircle2 className="w-5 h-5 text-[#6B8E6B]" />
              <p className="text-[#6B8E6B]">All clear — no immediate actions required</p>
            </div>
          )}
        </div>
      </div>

      {/* Stats + Net Worth Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Quick Stats */}
        <div className="caffeine-card p-6" data-testid="quick-stats">
          <h3 className="font-heading text-lg text-[#2C2420] mb-4">Quick Stats</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="text-center p-3 bg-[#F9F7F5] rounded-lg">
              <Building2 className="w-5 h-5 mx-auto text-[#4A3B32] mb-2" />
              <p className="text-2xl font-semibold text-[#2C2420] tabular-nums">{displayClient?.stats?.entities || 0}</p>
              <p className="text-xs text-[#8C8078]">Entities</p>
            </div>
            <div className="text-center p-3 bg-[#F9F7F5] rounded-lg">
              <Users className="w-5 h-5 mx-auto text-[#4A3B32] mb-2" />
              <p className="text-2xl font-semibold text-[#2C2420] tabular-nums">{displayClient?.stats?.individuals || 0}</p>
              <p className="text-xs text-[#8C8078]">Individuals</p>
            </div>
            <div className="text-center p-3 bg-[#F9F7F5] rounded-lg">
              <Link2 className="w-5 h-5 mx-auto text-[#6B8E6B] mb-2" />
              <p className="text-2xl font-semibold text-[#2C2420] tabular-nums">{displayClient?.stats?.linksVerified || 0}</p>
              <p className="text-xs text-[#8C8078]">Links Verified</p>
            </div>
            <div className="text-center p-3 bg-[#F9F7F5] rounded-lg">
              <Clock className="w-5 h-5 mx-auto text-[#D99A6C] mb-2" />
              <p className="text-2xl font-semibold text-[#2C2420] tabular-nums">{displayClient?.stats?.tasksDue || 0}</p>
              <p className="text-xs text-[#8C8078]">Tasks Due</p>
            </div>
          </div>
        </div>

        {/* Net Worth */}
        {!displayClient?.restricted && (
          <div className="caffeine-card p-6" data-testid="net-worth-card">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-lg text-[#2C2420]">Total Group Net Worth</h3>
              {!editingNetWorth ? (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setEditingNetWorth(true)}
                  className="caffeine-btn-ghost"
                  data-testid="edit-net-worth-btn"
                >
                  <Edit2 className="w-4 h-4 mr-1" />
                  Edit
                </Button>
              ) : (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={handleSaveNetWorth}
                  className="caffeine-btn-ghost text-[#6B8E6B]"
                  data-testid="save-net-worth-btn"
                >
                  <Save className="w-4 h-4 mr-1" />
                  Save
                </Button>
              )}
            </div>
            
            {editingNetWorth ? (
              <div className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-[#8C8078]" />
                <Input
                  type="number"
                  value={netWorthValue}
                  onChange={(e) => setNetWorthValue(parseFloat(e.target.value) || 0)}
                  className="caffeine-input"
                  data-testid="net-worth-input"
                />
              </div>
            ) : (
              <p className="text-3xl font-semibold text-[#2C2420] tabular-nums">
                {formatCurrency(displayClient?.netWorth)}
              </p>
            )}
            
            <p className="text-xs text-[#8C8078] mt-3">
              Last updated: {formatDateTimeAU(displayClient?.netWorthUpdated)}
            </p>
            <p className="text-xs text-[#8C8078] mt-1">
              Indicative, maintained by team. Documents stored securely; external sources indexed.
            </p>
          </div>
        )}
      </div>

      {/* Internal Team (Lead + Supporting Advisers) */}
      <div className="mb-6">
        <InternalAdviserAssignment
          clientId={clientId}
          client={displayClient}
          onUpdated={async () => {
            try {
              const refreshed = await api.getClient(token, clientId);
              setLocalClient(refreshed.data);
            } catch (e) {
              logger.warn('ClientOverview: refresh after update failed', e);
            }
          }}
        />
      </div>

      {/* Data Completeness */}
      <div className="caffeine-card p-6 mb-6" data-testid="data-completeness">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading text-lg text-[#2C2420]">Data Completeness</h3>
          <span className="text-2xl font-semibold text-[#4A3B32] tabular-nums">{overallCompleteness}%</span>
        </div>
        <div className="space-y-4">
          {Object.entries(completeness).map(([key, value]) => (
            <div key={key}>
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm text-[#5D524D] capitalize">{key}</span>
                <span className="text-sm font-medium text-[#2C2420] tabular-nums">{value}%</span>
              </div>
              <Progress 
                value={value} 
                className="h-2 bg-[#E6E0D9]"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Key Documents */}
      <div className="caffeine-card p-6 mb-6" data-testid="key-documents">
        <h3 className="font-heading text-lg text-[#2C2420] mb-4">Key Documents</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {governance.slice(0, 6).map(doc => (
            <div key={doc.id} className="doc-card">
              <div className="flex items-start gap-3 mb-3">
                <FileText className="w-5 h-5 text-[#4A3B32] flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-[#2C2420] text-sm">{doc.title}</p>
                  <p className="text-xs text-[#8C8078]">{doc.category}</p>
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="sm"
                className="w-full caffeine-btn-ghost text-xs justify-center"
              >
                <ExternalLink className="w-3 h-3 mr-1" />
                Open in source system
              </Button>
              <p className="text-xs text-[#8C8078] text-center mt-2">Indexed, not stored</p>
            </div>
          ))}
        </div>
      </div>

      {/* Notes & Call Log Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Notes */}
        <NotesBlock
          notes={displayClient?.notes}
          updatedAt={displayClient?.notesUpdatedAt}
          updatedBy={displayClient?.notesUpdatedBy}
          onSave={handleSaveNotes}
          placeholder="Add notes about this client group..."
        />

        {/* Call Log */}
        <CallLogCard
          callLogs={callLogs}
          onViewAll={() => navigate(`/app/clients/${clientId}/activity`)}
          onAddNew={() => { setEditingCallLog(null); setShowCallLogModal(true); }}
          onEdit={handleEditCallLog}
          onDelete={handleDeleteCallLog}
          onCreateTask={handleCreateTaskFromCall}
          onSendSummary={handleSendCallSummary}
          maxDisplay={1}
          title="Latest Call"
        />

        {/* AI Client Insights */}
        <AIClientInsights 
          clientId={clientId} 
          clientName={displayClient?.name}
        />
      </div>

      {/* Recent Activity */}
      <div className="caffeine-card p-6" data-testid="recent-activity">
        <h3 className="font-heading text-lg text-[#2C2420] mb-4">Recent Changes</h3>
        <div className="space-y-3">
          {activity.slice(0, 5).map(item => (
            <div key={item.id} className="flex items-start gap-3 py-2 border-b border-[#E6E0D9] last:border-0">
              <div className="w-8 h-8 bg-[#4A3B32]/10 rounded-full flex items-center justify-center text-[#4A3B32] text-xs font-medium flex-shrink-0">
                {item.actor.split(' ').map(n => n[0]).join('')}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[#2C2420]">
                  <span className="font-medium">{item.actor}</span>
                  {' '}{item.action.toLowerCase()}{' '}
                  <span className="text-[#4A3B32]">{item.object}</span>
                </p>
                <p className="text-xs text-[#8C8078]">{item.outcome}</p>
              </div>
              <span className="text-xs text-[#8C8078] whitespace-nowrap">
                {formatDateTimeAU(item.timestamp)}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Call Log Modal */}
      <CallLogModal
        isOpen={showCallLogModal}
        onClose={() => { setShowCallLogModal(false); setEditingCallLog(null); }}
        onSave={handleUpdateCallLog}
        editingCallLog={editingCallLog}
        individuals={individuals}
        advisers={advisers}
      />

      {showTaskModal && (
        <TaskModal
          task={prefillTaskData}
          onClose={() => { setShowTaskModal(false); setPrefillTaskData(null); }}
          onSave={handleSaveTask}
          entities={entities}
          individuals={individuals}
        />
      )}
    </div>
  );
}
